import {
  canonicalPayorByID,
  canonicalTariffByID,
  claimStatusValues,
  payorRefs,
  tariffRefs,
} from './finance';
import { canonicalSiteByID, siteRefs } from './organizations';
import {
  canonicalHealthcardByID,
  canonicalPatientByID,
  canonicalProviderByID,
  healthcardRefs,
  patientRefs,
  providerRefs,
} from './people';
import { canonicalSubmissionByID } from './submissions';
import { CanonicalClaim, CanonicalDebit, normalizeClaim } from './shared';

const debitDefaults: Omit<
  CanonicalDebit,
  | 'id'
  | 'claimID'
  | 'payorID'
  | 'requestedAmount'
  | 'paidAmount'
  | 'microfilm'
  | 'createdAt'
  | 'status'
  | 'type'
  | 'source'
  | 'providerID'
  | 'tariffID'
> = {
  payor: undefined,
  provider: undefined,
  eobs: [],
  claim: undefined,
  claimHistory: undefined,
  tariff: undefined,
  submission: undefined,
};

const makeDebitSeed = (
  debit: Pick<
    CanonicalDebit,
    | 'id'
    | 'claimID'
    | 'payorID'
    | 'requestedAmount'
    | 'paidAmount'
    | 'microfilm'
    | 'createdAt'
    | 'status'
    | 'type'
    | 'source'
    | 'providerID'
    | 'tariffID'
  > &
    Partial<CanonicalDebit>
): CanonicalDebit => ({
  ...debitDefaults,
  ...debit,
});

const claimDefaults: Omit<
  CanonicalClaim,
  | 'id'
  | 'version'
  | 'siteID'
  | 'createdAt'
  | 'providerID'
  | 'patientID'
  | 'site'
  | 'status'
  | 'patient'
  | 'provider'
  | 'tariffs'
  | 'overhead'
  | 'group'
> = {
  referringProvider: null,
  createdBy: null,
  updatedAt: undefined,
  claimNumber: undefined,
  providerComments: null,
  clerkComments: null,
  internalComments: null,
  providerNotes: null,
  chiroService: null,
  icd9: null,
  interpretingRadiologist: null,
  locationOfService: null,
  optometristReason: null,
  patientCount: 1,
  serviceLocationIndicator: null,
  virtualPhysicianRegion: undefined,
  thirdPartyLiability: null,
  incorporated: '0',
  serviceStart: undefined,
  serviceEnd: undefined,
  healthcardID: null,
  payorID: undefined,
  resubmit: undefined,
  hospitalName: '',
  hospitalCode: '',
  facilityNumber: '',
  credit: undefined,
  intakeItemID: undefined,
  healthcard: undefined,
  createdByUser: undefined,
  payor: undefined,
  referringProviderObject: undefined,
  intakeItem: undefined,
  debits: [],
  claimHistories: [],
  submissions: [],
  remittances: undefined,
  invoices: undefined,
  actionRequests: [],
  actionResponses: [],
  messageID: '',
  groupID: null,
};

const makeClaimSeed = (
  claim: Pick<
    CanonicalClaim,
    | 'id'
    | 'version'
    | 'siteID'
    | 'createdAt'
    | 'providerID'
    | 'patientID'
    | 'site'
    | 'status'
    | 'patient'
    | 'provider'
    | 'tariffs'
    | 'overhead'
    | 'group'
  > &
    Partial<CanonicalClaim>
): CanonicalClaim => ({
  ...claimDefaults,
  ...claim,
});

const claimSeeds = [
  makeClaimSeed({
    id: '5563a744-9a0a-41d6-89b9-c6f5002cc3de',
    version: 4,

    siteID: siteRefs.qdocVirtualHealthcare.id,
    createdAt: '2025-02-18T23:26:59.890Z',
    claimNumber: '000252199',
    providerComments: null,
    clerkComments: null,
    chiroService: '212',
    interpretingRadiologist: null,
    locationOfService: 'B',
    optometristReason: null,
    patientCount: 1,
    referringProvider: '00000',
    providerID: providerRefs.adamDonen.id,
    patientID: patientRefs.adamLowry.id,
    serviceLocationIndicator: 'C',
    thirdPartyLiability: null,
    incorporated: '0',
    serviceStart: '2025-02-18T23:21:34.000Z',
    serviceEnd: '2025-02-18T23:22:36.000Z',
    updatedAt: '2025-03-04T00:25:42.749Z',
    virtualPhysicianRegion: 'W',
    overhead: 15.0,
    internalComments: null,
    icd9: 'E9794',
    providerNotes: null,
    healthcardID: healthcardRefs.adamLowryPrimary.id,
    payorID: payorRefs.mb.id,
    healthcard: canonicalHealthcardByID.get(
      healthcardRefs.adamLowryPrimary.id
    )!,
    patient: canonicalPatientByID.get(patientRefs.adamLowry.id)!,
    provider: canonicalProviderByID.get(providerRefs.adamDonen.id)!,
    site: canonicalSiteByID.get(siteRefs.qdocVirtualHealthcare.id)!,
    status: claimStatusValues.paid,
    payor: canonicalPayorByID.get(payorRefs.mb.id)!,
    tariffs: [
      canonicalTariffByID.get(tariffRefs.testing8442.id)!,
      canonicalTariffByID.get(tariffRefs.alcohol9006.id)!,
    ],
    submissions: [
      canonicalSubmissionByID.get('d2a97903-066e-42a5-aff0-4aadffca0da0')!,
    ],
    debits: [
      makeDebitSeed({
        id: '586569c9-abd8-4511-b135-79e6f29fb079',
        claimID: '5563a744-9a0a-41d6-89b9-c6f5002cc3de',
        payorID: payorRefs.mb.id,
        requestedAmount: '104.07',
        paidAmount: '101.53',
        microfilm: '03123799',
        createdAt: '2025-02-07T00:28:51.172Z',
        status: 'processed',
        type: 'PAYMENT',
        source: 'REMITTANCE',
        providerID: providerRefs.weezerRockMusic.id,
        payor: canonicalPayorByID.get(payorRefs.mb.id)!,
        provider: canonicalProviderByID.get(providerRefs.weezerRockMusic.id)!,
        tariffID: tariffRefs.virtual8447.id,
        eobs: [],
      }),
      makeDebitSeed({
        id: '7aec5cf2-6528-4fe4-ad4a-f6fe5049c5d1',
        claimID: '5563a744-9a0a-41d6-89b9-c6f5002cc3de',
        payorID: payorRefs.mb.id,
        requestedAmount: '53.34',
        paidAmount: '50.77',
        microfilm: '03123799',
        createdAt: '2025-02-07T00:28:51.491Z',
        status: 'processed',
        type: 'PAYMENT',
        source: 'REMITTANCE',
        providerID: providerRefs.weezerRockMusic.id,
        payor: canonicalPayorByID.get(payorRefs.mb.id)!,
        provider: canonicalProviderByID.get(providerRefs.weezerRockMusic.id)!,
        tariffID: tariffRefs.premium5550.id,
        eobs: [],
      }),
    ],
    group: null,
  }),
  makeClaimSeed({
    id: 'f0c4dbd4-3dbf-4bc7-b212-a2f5d378f5d3',
    version: 2,

    siteID: siteRefs.qdocVirtualHealthcare.id,
    createdAt: '2025-01-12T03:58:32.661Z',
    claimNumber: '000250097',
    providerComments:
      'Fever for 4 days.  axillary temp 38.3.  fever now gone and then got a rash.',
    clerkComments: 'URGENT',
    chiroService: null,
    interpretingRadiologist: null,
    locationOfService: 'W',
    optometristReason: null,
    patientCount: 1,
    referringProvider: '00000',
    providerID: providerRefs.weezerRockMusic.id,
    patientID: patientRefs.adamLowry.id,
    serviceLocationIndicator: 'O',
    thirdPartyLiability: null,
    incorporated: '0',
    serviceStart: '2025-01-12T03:40:12.000Z',
    serviceEnd: '2025-01-12T03:47:32.000Z',
    updatedAt: '2025-03-08T02:31:49.941Z',
    virtualPhysicianRegion: 'W',
    overhead: 15.0,
    internalComments: null,
    icd9: '05810',
    providerNotes: null,
    healthcardID: healthcardRefs.adamLowryPrimary.id,
    payorID: payorRefs.mb.id,
    healthcard: canonicalHealthcardByID.get(
      healthcardRefs.adamLowryPrimary.id
    )!,
    patient: canonicalPatientByID.get(patientRefs.adamLowry.id)!,
    provider: canonicalProviderByID.get(providerRefs.weezerRockMusic.id)!,
    site: canonicalSiteByID.get(siteRefs.qdocVirtualHealthcare.id)!,
    status: claimStatusValues.queried,
    payor: canonicalPayorByID.get(payorRefs.mb.id)!,
    tariffs: [
      canonicalTariffByID.get(tariffRefs.testing8442.id)!,
      canonicalTariffByID.get(tariffRefs.alcohol9006.id)!,
    ],
    submissions: [],
    debits: [
      makeDebitSeed({
        id: 'd015a101-debit-1',
        claimID: 'f0c4dbd4-3dbf-4bc7-b212-a2f5d378f5d3',
        payorID: payorRefs.mb.id,
        requestedAmount: '40.00',
        paidAmount: '38.00',
        microfilm: '04111111',
        createdAt: '2025-02-01T11:00:00.000Z',
        status: 'processed',
        type: 'PAYMENT',
        source: 'REMITTANCE',
        providerID: providerRefs.weezerRockMusic.id,
        payor: canonicalPayorByID.get(payorRefs.mb.id)!,
        provider: canonicalProviderByID.get(providerRefs.weezerRockMusic.id)!,
        tariffID: tariffRefs.testing8442.id,
        eobs: [],
      }),
    ],
    group: null,
  }),
  makeClaimSeed({
    id: '5130b5c9-5266-4de5-bbb6-02b1eaef5cdf',
    version: 12,

    siteID: siteRefs.qdocVirtualHealthcare.id,
    createdAt: '2025-01-11T03:09:51.903Z',
    claimNumber: '000252264',
    providerComments: null,
    clerkComments: null,
    chiroService: null,
    interpretingRadiologist: null,
    locationOfService: null,
    optometristReason: null,
    patientCount: 1,
    referringProvider: '12345',
    providerID: providerRefs.normanSilver.id,
    patientID: patientRefs.adamLowry.id,
    serviceLocationIndicator: null,
    thirdPartyLiability: null,
    incorporated: '0',
    serviceStart: '2025-01-11T03:00:51.088Z',
    serviceEnd: '2025-01-11T03:00:51.088Z',
    updatedAt: '2025-03-03T22:24:50.417Z',
    virtualPhysicianRegion: 'W',
    overhead: 0.0,
    internalComments: null,
    icd9: null,
    providerNotes: null,
    healthcardID: healthcardRefs.adamLowryPrimary.id,
    payorID: payorRefs.msh.id,
    healthcard: canonicalHealthcardByID.get(
      healthcardRefs.adamLowryPrimary.id
    )!,
    patient: canonicalPatientByID.get(patientRefs.adamLowry.id)!,
    provider: canonicalProviderByID.get(providerRefs.normanSilver.id)!,
    site: canonicalSiteByID.get(siteRefs.qdocVirtualHealthcare.id)!,
    status: claimStatusValues.paid,
    payor: canonicalPayorByID.get(payorRefs.msh.id)!,
    tariffs: [
      canonicalTariffByID.get(tariffRefs.testing8442.id)!,
      canonicalTariffByID.get(tariffRefs.alcohol9006.id)!,
    ],
    submissions: [],
    debits: [
      makeDebitSeed({
        id: '5130b5c9-debit-1',
        claimID: '5130b5c9-5266-4de5-bbb6-02b1eaef5cdf',
        payorID: payorRefs.msh.id,
        requestedAmount: '75.00',
        paidAmount: '70.00',
        microfilm: '05123456',
        createdAt: '2025-01-15T10:00:00.000Z',
        status: 'processed',
        type: 'PAYMENT',
        source: 'REMITTANCE',
        providerID: providerRefs.normanSilver.id,
        payor: canonicalPayorByID.get(payorRefs.msh.id)!,
        provider: canonicalProviderByID.get(providerRefs.normanSilver.id)!,
        tariffID: tariffRefs.testing8442.id,
        eobs: [],
      }),
    ],
    group: null,
  }),
  makeClaimSeed({
    id: '326619d4-9fbc-43d9-9f1a-3534cc7ef2d1',
    version: 1,

    siteID: siteRefs.canadaLifeCentre.id,
    createdAt: '2024-02-10T03:22:34.000Z',
    claimNumber: '000005364',
    providerComments: 'Million language project nothing.',
    clerkComments: 'Foot get interview level community.',
    chiroService: 'B01',
    icd9: '75264',
    interpretingRadiologist: '03069',
    locationOfService: 'C',
    optometristReason: '26',
    patientCount: 1,
    referringProvider: '00000',
    providerID: providerRefs.brettsNurse.id,
    patientID: patientRefs.ronWeasley.id,
    serviceLocationIndicator: 'N',
    thirdPartyLiability: null,
    incorporated: '0',
    serviceStart: '2024-02-10T03:22:34.000Z',
    serviceEnd: '2024-02-10T03:22:34.000Z',
    updatedAt: '2024-02-10T03:22:34.000Z',
    healthcardID: healthcardRefs.ronWeasleyPrimary.id,
    payorID: payorRefs.msh.id,
    healthcard: canonicalHealthcardByID.get(
      healthcardRefs.ronWeasleyPrimary.id
    )!,
    patient: canonicalPatientByID.get(patientRefs.ronWeasley.id)!,
    provider: canonicalProviderByID.get(providerRefs.brettsNurse.id)!,
    site: canonicalSiteByID.get(siteRefs.canadaLifeCentre.id)!,
    status: claimStatusValues.ready,
    payor: canonicalPayorByID.get(payorRefs.msh.id)!,
    tariffs: [
      canonicalTariffByID.get(tariffRefs.virtual8321.id)!,
      canonicalTariffByID.get(tariffRefs.modifier2789.id)!,
    ],
    submissions: [],
    debits: [
      makeDebitSeed({
        id: '326619d4-debit-1',
        claimID: '326619d4-9fbc-43d9-9f1a-3534cc7ef2d1',
        payorID: payorRefs.msh.id,
        requestedAmount: '55.00',
        paidAmount: '50.00',
        microfilm: '06123456',
        createdAt: '2024-02-11T03:22:34.000Z',
        status: 'processed',
        type: 'PAYMENT',
        source: 'REMITTANCE',
        providerID: providerRefs.brettsNurse.id,
        payor: canonicalPayorByID.get(payorRefs.msh.id)!,
        provider: canonicalProviderByID.get(providerRefs.brettsNurse.id)!,
        tariffID: tariffRefs.virtual8321.id,
        eobs: [],
      }),
    ],
    overhead: 15,
    group: null,
  }),
  makeClaimSeed({
    id: 'd015a101-7a06-4071-9fec-17ee687acfbf',
    version: 1,

    siteID: siteRefs.canadaLifeCentre.id,
    createdAt: '2024-06-27T09:17:03.000Z',
    claimNumber: '000003261',
    providerComments: 'Own image society scene price contain rate.',
    clerkComments: 'Hard character listen should leader room.',
    chiroService: null,
    icd9: '75264',
    interpretingRadiologist: null,
    locationOfService: 'O',
    optometristReason: '22',
    patientCount: 1,
    referringProvider: '00000',
    providerID: providerRefs.lebronJames.id,
    patientID: patientRefs.kyleConnor.id,
    serviceLocationIndicator: 'B',
    thirdPartyLiability: null,
    incorporated: '0',
    serviceStart: '2024-06-27T09:17:03.000Z',
    serviceEnd: '2024-06-27T09:17:03.000Z',
    updatedAt: '2024-06-27T09:17:03.000Z',
    healthcardID: healthcardRefs.kyleConnorPrimary.id,
    payorID: payorRefs.msh.id,
    healthcard: canonicalHealthcardByID.get(
      healthcardRefs.kyleConnorPrimary.id
    )!,
    patient: canonicalPatientByID.get(patientRefs.kyleConnor.id)!,
    provider: canonicalProviderByID.get(providerRefs.lebronJames.id)!,
    site: canonicalSiteByID.get(siteRefs.canadaLifeCentre.id)!,
    status: claimStatusValues.paid,
    payor: canonicalPayorByID.get(payorRefs.msh.id)!,
    tariffs: [canonicalTariffByID.get(tariffRefs.virtual8321.id)!],
    submissions: [],
    debits: [
      makeDebitSeed({
        id: 'd015a101-paid-debit-1',
        claimID: 'd015a101-7a06-4071-9fec-17ee687acfbf',
        payorID: payorRefs.msh.id,
        requestedAmount: '65.00',
        paidAmount: '60.00',
        microfilm: '07123456',
        createdAt: '2024-06-28T09:17:03.000Z',
        status: 'processed',
        type: 'PAYMENT',
        source: 'REMITTANCE',
        providerID: providerRefs.lebronJames.id,
        payor: canonicalPayorByID.get(payorRefs.msh.id)!,
        provider: canonicalProviderByID.get(providerRefs.lebronJames.id)!,
        tariffID: tariffRefs.virtual8321.id,
        eobs: [],
      }),
    ],
    overhead: 15,
    group: null,
  }),
] satisfies CanonicalClaim[];

export const canonicalClaims: CanonicalClaim[] = claimSeeds.map(normalizeClaim);
